import { createSlice, PayloadAction } from '@reduxjs/toolkit'

/* eslint no-param-reassign: 0 */
import { Bundle, GenericError, BundleType } from '../model/bundles.model'

interface BundleCollection {
  [BundleType.PROMOS]: {
    errorMessage: string
    errorCode: string
    data: Bundle[]
    selected: number[]
    loading: boolean | null
  }
  [BundleType.BONUS]: {
    errorMessage: string
    errorCode: string
    data: Bundle[]
    selected: number[]
    loading: boolean | null
  }
}

interface SuccessBundlesList {
  bundles: Bundle[]
  id: string
  type: BundleType
}

export interface BundlesState {
  [key: string]: BundleCollection
}

interface ErrorBundlesList extends GenericError {
  id: string
  type: BundleType
}

interface SetSelectedBundes {
  id: string
  bundles: number[]
  type: BundleType
}

interface FetchBundles {
  id: string
  type: BundleType
  tariffId: string | number
  tariffPrice?: number
}

export const bundlesInitialState = {
  errorMessage: '',
  errorCode: '',
  data: [],
  selected: [],
  loading: null,
}

export const bundlesStructure: BundleCollection = {
  [BundleType.BONUS]: bundlesInitialState,
  [BundleType.PROMOS]: bundlesInitialState,
}

export const initialState: BundlesState = {
  mainLine: bundlesStructure,
}

export const bundlesSlice = createSlice({
  name: 'bundles',
  initialState,
  reducers: {
    createBundlesStructure(state, action: PayloadAction<string>) {
      const { payload } = action
      return {
        ...state,
        [payload]: bundlesStructure,
      }
    },
    deleteBundle: (state, action: PayloadAction<string>) => {
      const { payload } = action
      delete state[payload]
      return state
    },
    fetchBundles: (state, _action: PayloadAction<FetchBundles>) => {
      const {
        payload: { id, type },
      } = _action
      return {
        ...state,
        [id]: { ...state[id], [type]: { ...bundlesStructure[type], loading: true } },
      }
    },
    fetchBundlesSuccess: (state, action: PayloadAction<SuccessBundlesList>) => {
      const {
        payload: { id, bundles, type },
      } = action
      return {
        ...state,
        [id]: {
          ...state[id],
          [type]: { ...bundlesStructure[type], loading: false, data: bundles },
        },
      }
    },
    fetchBundlesError: (state, action: PayloadAction<ErrorBundlesList>) => {
      const {
        payload: { code, message, id, type },
      } = action
      return {
        ...state,
        [id]: {
          ...state[id],
          [type]: {
            ...bundlesStructure[type],
            loading: false,
            errorCode: code || '500',
            errorMessage: message || 'Error',
          },
        },
      }
    },
    setSelectedBundles: (state, action: PayloadAction<SetSelectedBundes>) => {
      const {
        payload: { id, bundles, type },
      } = action
      return {
        ...state,
        [id]: { ...state[id], [type]: { ...state[id][type], selected: bundles } },
      }
    },
    resetBundles() {
      return initialState
    },
  },
})

export const {
  fetchBundles,
  fetchBundlesSuccess,
  fetchBundlesError,
  setSelectedBundles,
  createBundlesStructure,
  deleteBundle,
  resetBundles,
} = bundlesSlice.actions

const bundlesReducer = bundlesSlice.reducer

export default bundlesReducer
